import { useState } from 'react';
import { 
  ArrowLeft, ArrowRight, Check, User, Image, Palette, 
  LayoutGrid, Sparkles, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Modules', icon: LayoutGrid },
  { id: 3, title: 'Theme', icon: Palette },
  { id: 4, title: 'Create', icon: Sparkles },
];

const THEMES = [
  { id: 'dark_stage', name: 'Dark Stage', colors: { bg: '#0F172A', primary: '#F06415', text: '#FFFFFF' } },
  { id: 'theatre_red', name: 'Theatre Red', colors: { bg: '#1A0C0E', primary: '#B91C1C', text: '#F8F0E6' } },
  { id: 'ivory_gold', name: 'Ivory & Gold', colors: { bg: '#FAF7F1', primary: '#B08D2F', text: '#1C1710' } },
  { id: 'light_modern', name: 'Light Modern', colors: { bg: '#FFFFFF', primary: '#3B82F6', text: '#1E293B' } },
  { id: 'colorful_kids', name: 'Colorful Kids', colors: { bg: '#FEF3C7', primary: '#EC4899', text: '#7C3AED' } },
  { id: 'elegant_classic', name: 'Elegant Classic', colors: { bg: '#1C1917', primary: '#D4AF37', text: '#FAFAF9' } },
  { id: 'bold_artist', name: 'Bold Artist', colors: { bg: '#18181B', primary: '#EF4444', text: '#FFFFFF' } },
];

const MODULES = [
  { id: 'home', name: 'Home', description: 'Hero section', packages: ['starter', 'pro', 'max'], required: true },
  { id: 'about', name: 'About', description: 'Bio & profile', packages: ['starter', 'pro', 'max'] },
  { id: 'gallery', name: 'Gallery', description: 'Image gallery', packages: ['starter', 'pro', 'max'] },
  { id: 'video_gallery', name: 'Video Gallery', description: 'YouTube videos', packages: ['pro', 'max'] },
  { id: 'timetable', name: 'Timetable', description: 'Weekly class schedule', packages: ['pro', 'max'] },
  { id: 'events', name: 'Events', description: 'Tour dates', packages: ['pro', 'max'] },
  { id: 'news', name: 'News', description: 'News & updates', packages: ['max'] },
  { id: 'music', name: 'Music', description: 'Spotify embed', packages: ['pro', 'max'] },
  { id: 'contact', name: 'Contact', description: 'Contact form', packages: ['starter', 'pro', 'max'] },
  { id: 'social_links', name: 'Social', description: 'Social links', packages: ['starter', 'pro', 'max'] },
  { id: 'mailing_list', name: 'Mailing List', description: 'Newsletter signup', packages: ['max'] },
];

export default function SiteBuilder({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({
    performer_name: '',
    tagline: '',
    service_type: '',
    package: 'pro',
    theme: 'dark_stage',
    enabled_modules: ['home', 'about', 'gallery', 'contact', 'social_links'],
    primary_color: '#F06415',
  });

  const availableModules = MODULES.filter(m => m.packages.includes(form.package));

  const handleModuleToggle = (moduleId) => {
    const module = MODULES.find(m => m.id === moduleId);
    if (module?.required) return;
    
    setForm(prev => ({
      ...prev,
      enabled_modules: prev.enabled_modules.includes(moduleId)
        ? prev.enabled_modules.filter(id => id !== moduleId)
        : [...prev.enabled_modules, moduleId]
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      if (!form.performer_name) {
        toast.error('Please enter a performer name');
        return;
      }
    }
    setStep(s => Math.min(s + 1, 4));
  };

  const handleBack = () => {
    setStep(s => Math.max(s - 1, 1));
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const response = await api.post('/sites', {
        performer_name: form.performer_name,
        tagline: form.tagline,
        service_type: form.service_type,
        package: form.package,
      });

      // Update theme and modules
      const siteId = response.data.id;
      await api.put(`/sites/${siteId}`, {
        theme: {
          style: form.theme,
          primary_color: form.primary_color,
        },
        enabled_modules: form.enabled_modules,
      });

      toast.success('Site created successfully!');
      onComplete?.(siteId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-orange-500" />
            <span className="font-semibold text-white">Create New Site</span>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">
            Cancel
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full transition-all",
                  step === s.id ? "bg-orange-500 text-white" : 
                  step > s.id ? "bg-emerald-500/20 text-emerald-400" : 
                  "bg-slate-700 text-slate-400"
                )}>
                  {step > s.id ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                  <span className="text-sm font-medium hidden sm:inline">{s.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={cn(
                    "w-12 h-0.5 mx-2",
                    step > s.id ? "bg-emerald-500" : "bg-slate-700"
                  )} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Basic Information</CardTitle>
              <CardDescription>Tell us about the performer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-slate-200">Performer Name *</Label>
                <Input 
                  value={form.performer_name}
                  onChange={(e) => setForm({...form, performer_name: e.target.value})}
                  placeholder="e.g. John Smith"
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="builder-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Tagline</Label>
                <Input 
                  value={form.tagline}
                  onChange={(e) => setForm({...form, tagline: e.target.value})}
                  placeholder="e.g. Award-winning vocalist"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Main Service</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({...form, service_type: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Singer">Singer</SelectItem>
                    <SelectItem value="DJ">DJ</SelectItem>
                    <SelectItem value="Magician">Magician</SelectItem>
                    <SelectItem value="Band">Band</SelectItem>
                    <SelectItem value="Comedian">Comedian</SelectItem>
                    <SelectItem value="Children's Entertainer">Children's Entertainer</SelectItem>
                    <SelectItem value="Actor">Actor</SelectItem>
                    <SelectItem value="Musician">Musician</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Package</Label>
                <RadioGroup value={form.package} onValueChange={(v) => setForm({...form, package: v})} className="grid grid-cols-3 gap-3">
                  {['starter', 'pro', 'max'].map(pkg => (
                    <Label 
                      key={pkg}
                      className={cn(
                        "flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all",
                        form.package === pkg 
                          ? "bg-orange-500/20 border-orange-500 text-white" 
                          : "bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500"
                      )}
                    >
                      <RadioGroupItem value={pkg} className="sr-only" />
                      <span className="font-semibold capitalize">{pkg}</span>
                      <span className="text-xs text-slate-400">
                        {pkg === 'starter' ? '5 modules' : pkg === 'pro' ? '8 modules' : '10 modules'}
                      </span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Modules */}
        {step === 2 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Select Modules</CardTitle>
              <CardDescription>Choose which sections to include on the site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {MODULES.map(module => {
                  const available = module.packages.includes(form.package);
                  const enabled = form.enabled_modules.includes(module.id);
                  return (
                    <div 
                      key={module.id}
                      onClick={() => available && handleModuleToggle(module.id)}
                      className={cn(
                        "p-4 rounded-lg border transition-all",
                        !available 
                          ? "bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed" 
                          : enabled
                            ? "bg-orange-500/20 border-orange-500 cursor-pointer"
                            : "bg-slate-700 border-slate-600 cursor-pointer hover:border-slate-500"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn("font-medium", enabled ? "text-white" : "text-slate-300")}>
                          {module.name}
                        </span>
                        {module.required && <span className="text-xs text-orange-400">Required</span>}
                        {!available && <span className="text-xs text-slate-500">Pro+</span>}
                      </div>
                      <span className="text-xs text-slate-400">{module.description}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Theme */}
        {step === 3 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Choose Theme</CardTitle>
              <CardDescription>Select a visual style for the site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {THEMES.map(theme => (
                  <div 
                    key={theme.id}
                    onClick={() => setForm({...form, theme: theme.id, primary_color: theme.colors.primary})}
                    className={cn(
                      "p-4 rounded-lg border cursor-pointer transition-all",
                      form.theme === theme.id
                        ? "border-orange-500 ring-2 ring-orange-500/20"
                        : "border-slate-600 hover:border-slate-500"
                    )}
                    style={{ backgroundColor: theme.colors.bg }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full" 
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      <span style={{ color: theme.colors.text }} className="font-medium">
                        {theme.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-200">Primary Color</Label>
                <div className="flex items-center gap-3">
                  <input 
                    type="color" 
                    value={form.primary_color}
                    onChange={(e) => setForm({...form, primary_color: e.target.value})}
                    className="w-12 h-12 rounded cursor-pointer"
                  />
                  <Input 
                    value={form.primary_color}
                    onChange={(e) => setForm({...form, primary_color: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white w-32"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Create */}
        {step === 4 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Ready to Create</CardTitle>
              <CardDescription>Review your site configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Performer</span>
                  <span className="text-white font-medium">{form.performer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Package</span>
                  <span className="text-white font-medium capitalize">{form.package}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Theme</span>
                  <span className="text-white font-medium">{THEMES.find(t => t.id === form.theme)?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Modules</span>
                  <span className="text-white font-medium">{form.enabled_modules.length} selected</span>
                </div>
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={handleCreate} 
                  disabled={isCreating}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-6"
                  data-testid="create-site-submit"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Site
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={handleBack}
            disabled={step === 1}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {step < 4 && (
            <Button onClick={handleNext} className="bg-orange-500 hover:bg-orange-400">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
